package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.web.Route
import at.theatervogelweide.admin.web.Router
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

private val shell = Bem("admin-shell")
private val split = Bem("admin-split")
private val listPanel = Bem("admin-list-panel")
private val card = Bem("admin-card")

/**
 * The shell width from which list and editor sit side by side as panes (SplitView): a 64rem main column beside the
 * sidebar. The styles' container query on `.admin-shell` switches at the same width.
 */
const val PANES_MIN_WIDTH_REM = 78.5

/** Whether a shell [width] in pixels holds panes, with rem as [remPx] pixels. */
fun panesFit(width: Double, remPx: Double = rootFontSize()): Boolean = width >= PANES_MIN_WIDTH_REM * remPx

private fun rootFontSize(): Double =
    document.documentElement?.let { window.getComputedStyle(it).fontSize.removeSuffix("px").toDoubleOrNull() } ?: 16.0

/** Whether the shell is wide enough for panes; [AdminShell] provides it from its own width. */
val LocalPanes = compositionLocalOf { true }

/** Calls [onWidth] with [element]'s width whenever it changes, until the returned observer is disconnected. */
internal fun observeWidth(element: Element, onWidth: (Double) -> Unit): dynamic {
    val callback: (Array<dynamic>, dynamic) -> Unit = { _, _ -> onWidth(element.getBoundingClientRect().width) }
    // the element's own window's ResizeObserver, which the Kotlin DOM declarations lack; another window's misses frames
    val view: dynamic = element.ownerDocument?.defaultView ?: window
    val observer: dynamic = js("new view.ResizeObserver(callback)")
    observer.observe(element)
    return observer
}

/**
 * Moves a list-and-editor screen between its list and the editor of one record. [routeFor] is the screen's route for
 * a record, for a new one (`new`), or for the plain list (neither).
 *
 * With panes, the address follows the selection without history steps. As separate views, opening the editor adds a
 * history step, and going back returns to the list as it was left. Callers ask about unsaved changes first.
 */
class SplitController(
    private val router: Router,
    internal val guard: UnsavedChangesGuard,
    private val routeFor: (id: Long?, new: Boolean) -> Route,
) {
    /** As the shell was last laid out. */
    var panes: Boolean = true
        internal set

    /** The record that was open, highlighted for two seconds back on the list in the narrow layout. */
    var recent: Long? by mutableStateOf(null)
        internal set

    /** Where the window was scrolled to when the list made way for the editor. */
    internal var listScrollY: Double? = null

    val list: Route get() = routeFor(null, false)

    /** Opens the record [id] picked from the list. */
    fun open(id: Long) = openEditor(routeFor(id, false))

    /** Opens the editor of a new record. */
    fun openNew() = openEditor(routeFor(null, true))

    /** Puts the record [id], or the plain list, into the address without a history step: the first record, a saved new one. */
    fun show(id: Long?) = router.navigate(routeFor(id, false), replace = true)

    /** Leaves the editor: with panes for the first record, otherwise back to the list as it was left. */
    fun backToList() {
        when {
            panes -> show(null)
            router.previousIs(list) -> router.back()
            else -> router.navigate(list)
        }
    }

    private fun openEditor(route: Route) {
        when {
            panes -> router.navigate(route, replace = true)

            // from one editor to another, so the way back still leads to the list
            router.current != list -> router.navigate(route, replace = true)

            else -> {
                listScrollY = window.scrollY
                router.navigate(route)
            }
        }
    }
}

/** A [SplitController] for the screen at this place, which follows the shell's layout. */
@Composable
fun rememberSplitController(router: Router, guard: UnsavedChangesGuard, routeFor: (id: Long?, new: Boolean) -> Route): SplitController {
    val controller = remember(router, guard) { SplitController(router, guard, routeFor) }
    val panes = LocalPanes.current
    SideEffect { controller.panes = panes }
    return controller
}

/** Whether the editor is shown: always beside the list in panes, otherwise while the address names a record or a new one. */
@Composable
fun editorShown(selectedId: Long?, new: Boolean): Boolean = LocalPanes.current || selectedId != null || new

/** What [AdminSplitView] remembers between layouts, as last shown. */
private class SplitMemory {
    var editorView = false

    /** Whether anything was shown yet; a deep link stays at the page's top. */
    var shown = false
    var lastOpened: Long? = null
    var list: HTMLElement? = null
    var detail: HTMLElement? = null
}

/**
 * A list beside the editor of its selected record (the design system's SplitView), as the page body below the
 * [AdminPageHeader]. With panes both fill the window and scroll on their own; otherwise the list and the editor of
 * [selectedId] (or of a new record with [new]) are separate views, and the editor's starts with "← [name]".
 *
 * [listLabel] names the list card for screen readers. A [table] list takes the larger share of the width. The editor
 * card gets its save actions apart (`AdminCard(saveActions = …)`); [detailClasses] are added to the editor pane.
 */
@Composable
fun AdminSplitView(
    controller: SplitController,
    selectedId: Long?,
    new: Boolean,
    name: String,
    listLabel: String,
    table: Boolean = false,
    detailClasses: List<String> = emptyList(),
    list: @Composable () -> Unit,
    detail: @Composable () -> Unit,
) {
    val panes = LocalPanes.current
    val editorView = !panes && (selectedId != null || new)
    val memory = remember { SplitMemory() }
    // as shown last: an editor view that gives way to the list returns to it
    val returning = memory.editorView && !editorView && !panes
    if (returning) {
        controller.recent = memory.lastOpened
    }
    val firstShown = !memory.shown
    SideEffect {
        memory.shown = true
        memory.editorView = editorView
        if (editorView) {
            memory.lastOpened = selectedId
        }
    }

    LaunchedEffect(editorView) {
        when {
            editorView -> if (!firstShown) scrollMainColumnToTop(memory.detail)

            returning -> try {
                controller.listScrollY?.let { window.scrollTo(0.0, it) }
                memory.list?.let { focusRecentRow(it, scroll = controller.listScrollY == null) }
                delay(2.seconds)
            } finally {
                controller.recent = null
            }
        }
    }

    // a newly selected record's editor starts at its top; the list stays where it is
    LaunchedEffect(selectedId, new) {
        if (panes) {
            memory.detail?.scrollTop = 0.0
        }
    }

    Div({ classes(shell.element("body").className, shell.element("body").modifier("panes")) }) {
        Div({ classes(split.block, split.modifier("panes")) }) {
            if (editorView) {
                val list = controller.list
                A(list.path, {
                    classes(split.element("back").className)
                    onClick { event ->
                        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                            event.preventDefault()
                            controller.guard.proceed(controller::backToList)
                        }
                    }
                }) { Text("← $name") }
            }

            if (!editorView) {
                Section({
                    classes(*listOfNotNull(card.block, listPanel.block, split.element("list").className, split.element("list").modifier("wide").takeIf { table }).toTypedArray())
                    attr("aria-label", listLabel)
                    ref {
                        memory.list = it
                        onDispose { memory.list = null }
                    }
                }) { list() }
            }

            if (panes || editorView) {
                Div({
                    classes(*(listOfNotNull(split.element("detail").className, split.element("detail").modifier("narrow").takeIf { table }) + detailClasses).toTypedArray())
                    ref {
                        memory.detail = it
                        onDispose { memory.detail = null }
                    }
                }) { detail() }
            }
        }
    }
}

/**
 * The editor view opens at its top: the main column's, as the sidebar may sit above it. A page too short for that
 * yet, while the record loads, is scrolled once it has grown.
 */
private suspend fun scrollMainColumnToTop(detail: HTMLElement?) {
    val main = detail?.closest(".${shell.element("main").className}") ?: return
    repeat(EDITOR_LOAD_CHECKS) {
        val top = main.getBoundingClientRect().top + window.scrollY
        window.scrollTo(0.0, top)
        if (window.scrollY >= top - 1) {
            return
        }
        delay(100.milliseconds)
    }
}

private const val EDITOR_LOAD_CHECKS = 30

/** Focuses the row marked `data-recent`, scrolling it into view only when [scroll]. */
private fun focusRecentRow(list: HTMLElement, scroll: Boolean) {
    val row = list.querySelector("[data-recent=\"true\"]") ?: return
    val button = row as? HTMLButtonElement ?: row.querySelector("button") as? HTMLButtonElement ?: return
    val options: dynamic = js("({})")
    options.preventScroll = !scroll
    button.asDynamic().focus(options)
}
